import { useAtom } from "jotai";
import { AppStateAtom } from "../store";
import { checkToken } from "../api";

/**
 * Interface defining the functionality provided by the useUser hook
 */
interface UseUser {
  /** Refreshes the user profile data from the server */
  refreshUserProfile: () => void;

  /** Sets login information in state and localStorage */
  setLoginSettings: (email: string, theme: string, token: string) => void;

  /** Sets the loading state */
  setLoading: (loading: boolean) => void;

  /** Logs the current user out */
  logUserOut: () => void;
}

/**
 * Hook that provides user authentication and profile management functionality
 *
 * @returns An object with methods for managing user state
 *
 * @example
 * // Example usage
 * const { refreshUserProfile, logUserOut } = useUser();
 */
export const useUser = (): UseUser => {
  const [appState, setAppState] = useAtom(AppStateAtom);

  /**
   * Refreshes the user profile data from the server
   * Updates the application state with the latest user information
   */
  const refreshUserProfile = () => {
    checkToken().then((res) => {
      setAppState((prevState) => {
        return {
          ...prevState,
          user: res.user,
          subscriptionActive: res.user.subscriptionStatus,
          role: res.user.role,
        };
      });
    });
  };

  /**
   * Sets login information in state and localStorage
   *
   * @param email - The user's email address
   * @param theme - The user's preferred UI theme
   * @param token - The authentication token
   */
  const setLoginSettings = (email: string, theme: string, token: string) => {
    localStorage.setItem("token", token);
    setAppState((prevState) => {
      return {
        ...prevState,
        loading: false,
        errorMessage: "",
        email: email,
        authed: true,
        gridTheme: theme || "wb-ag-theme",
      };
    });
  };

  /**
   * Logs the current user out
   * Removes authentication token and resets application state
   */
  const logUserOut = () => {
    localStorage.removeItem("token");
    setAppState((prevState) => {
      return {
        ...prevState,
        authed: false,
        email: "",
        gridTheme: "wb-ag-theme",
        loading: false,
      };
    });
  };

  /**
   * Sets the loading state in the application
   *
   * @param loading - Boolean indicating if the application is loading
   */
  const setLoading = (loading: boolean) => {
    setAppState((prevState) => {
      return {
        ...prevState,
        loading: loading,
      };
    });
  };

  return {
    refreshUserProfile,
    setLoginSettings,
    setLoading,
    logUserOut,
  };
};
